import { Box, CircularProgress } from '@mui/material';
import React from 'react';
import { AdvisorAccessContextInstance } from '../../context/AdvisorAccessContext';
import { GlobalConfigProvider } from '../../context/GlobalConfigContext';
import { FeatureGate, FeatureType } from '../../context/FeatureContext';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { downloadPublishedPDF } from '../../services/DocUtils';
import EOISection from '../EOISection';
import FundOpportunityChat from '../FundOpportunityChat';
import FundOpportunityHeader from '../FundOpportunityHeader';
import LightboxManager from '../LightboxManager';
import StyledSection from '../styles/StyledSection';
import { StyledHeading, styledPageSX } from '../styles/styles';
import { FundOpportunityEOIInstance } from './controller';
import { FundOpportunityEOIFilters } from './FundOpportunityEOIFilters';
import { FundOpportunityEOILinkedWarning } from './FundOpportunityEOILinkedWarning';
import { FundOpportunityEOIPrintStyles } from './FundOpportunityEOIPrintStyles';
import { FundOpportunityEOIContentSections } from './FundOpportunityEOIContentSections';

export interface FundOpportunityEOIContentPanelProps {
  instance: FundOpportunityEOIInstance;
  contentManagementInterface: ContentManagementInterface;
}

export function FundOpportunityEOIContentPanel({
  instance,
  contentManagementInterface,
}: FundOpportunityEOIContentPanelProps): JSX.Element {
  return (
    <>
      <LightboxManager
        visible={!!instance.state.lightBoxVisible}
        requestMessage={instance.state.requestMessage}
        requestAppointment={instance.state.requestAppointment}
        requestShare={instance.state.requestShare}
        sendMessageResult={instance.state.sendMessageResult}
        sendMessageError={instance.state.sendMessageError}
        sendingMessage={instance.state.sendingMessage}
        onClose={() => instance.closeLightbox()}
        sendMessage={(message: string) => instance.submitMessage(message)}
        fundOpportunity={instance.state.fundOpportunity!}
      />{' '}
      <FundOpportunityEOIPrintStyles
        printDraft={instance.props.printDraft}
        printMode={instance.state.printMode}
      />
      <Box display="flex" className="fundOppWrapper" sx={styledPageSX}>
        <Box
          flex="1"
          sx={{
            flexGrow: { xs: 1 },
            maxWidth: { xs: '100%' },
          }}
          className="fundOppWrapper2"
        >
          <FundOpportunityHeader
            downloadPDF={() => downloadPublishedPDF(instance.state.fundOpportunity!)}
            showDownloadPdf={instance.state.staticPdfAvailable}
            tone={instance.props.tone}
            onCreateAppointment={() => instance.createAppointment()}
            onSendMessage={() => instance.sendMessage()}
            onShare={() => instance.share()}
            printMode={instance.state.printMode}
            fundOpportunity={instance.state.fundOpportunity!}
            investor={instance.state.investor!}
            advisingFor={instance.props.advisorMode ? instance.props.advisedForName : undefined}
          />
          {<FundOpportunityEOILinkedWarning instance={instance} />}
          <>
            <StyledSection tone="tone1" mb="4" additionalClassName="content-management-container">
              <StyledHeading>Opportunity Information</StyledHeading>
              <FundOpportunityEOIFilters instance={instance} />
              <FundOpportunityEOIContentSections
                instance={instance}
                contentManagementInterface={contentManagementInterface}
              />
            </StyledSection>

            {
              //          {instance.renderApplication()}
            }
            {!instance.props.referrer &&
              !instance.props.hideVera &&
              instance.state.fundOpportunity?.id &&
              (instance.state.filter === 'Vera' || instance.state.filter === 'All') && (
                <FeatureGate feature={FeatureType.FeatureAiChat}>
                  <StyledSection
                    tone="tone1"
                    bottomSpacing={4}
                    leftRightSpacing={0}
                    sx={{ marginBottom: '1.875rem', marginTop: '1.875rem' }}
                  >
                    {' '}
                    {/* Add some margin for spacing */}
                    <FundOpportunityChat
                      fundOpportunityId={instance.state.fundOpportunity.id}
                      advisorMode={instance.props.advisorMode}
                    />
                  </StyledSection>
                </FeatureGate>
              )}
            {(instance.state.filter === 'EOI' || instance.state.filter === 'All') &&
              !instance.props.hideEoiSection && (
                <>
                  {instance.props.referrer ? (
                    // Referral invitation mode
                    <GlobalConfigProvider>
                      <EOISection
                        downloadPDF={() => downloadPublishedPDF(instance.state.fundOpportunity!)}
                        investor={instance.state.investor}
                        fundOpportunity={instance.state.fundOpportunity!}
                        printMode={instance.state.printMode}
                        onSubmit={(investment, completedCallback) =>
                          instance.submitEoi(investment, completedCallback)
                        }
                        onWithdraw={(completedCallback) =>
                          instance.commitEOIWithdrawal(completedCallback)
                        }
                        referrer={instance.props.referrer}
                        onAcceptInvitation={(data) => instance.acceptInvitation(data)}
                        onRejectInvitation={() => instance.rejectInvitation()}
                        acceptingInvitation={instance.state.acceptingInvitation}
                      />
                    </GlobalConfigProvider>
                  ) : (
                    // Normal EOI mode
                    <GlobalConfigProvider>
                      <AdvisorAccessContextInstance.Consumer>
                        {(advisorAccessContext) => {
                          // In non-advisor mode, advisorAccessContext will be undefined
                          // This is expected and handled gracefully
                          const advisorRole =
                            instance.props.advisorMode &&
                            instance.state.investor?.contactId &&
                            advisorAccessContext
                              ? advisorAccessContext.getAdvisorRole(
                                  instance.state.investor.contactId,
                                )
                              : null;

                          // Don't render until custodian status is loaded (in non-admin mode)
                          if (
                            !instance.props.adminView &&
                            !instance.props.advisorMode &&
                            instance.state.loadingCustodianStatus
                          ) {
                            return (
                              <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                                <CircularProgress />
                              </Box>
                            );
                          }

                          return (
                            <EOISection
                              showDownloadPdf={instance.state.staticPdfAvailable}
                              downloadPDF={() =>
                                downloadPublishedPDF(instance.state.fundOpportunity!)
                              }
                              investor={instance.state.investor}
                              fundOpportunity={instance.state.fundOpportunity!}
                              printMode={instance.state.printMode}
                              onSubmit={(investment, completedCallback) =>
                                instance.submitEoi(investment, completedCallback)
                              }
                              onWithdraw={(completedCallback) =>
                                instance.commitEOIWithdrawal(completedCallback)
                              }
                              advisingFor={
                                instance.props.advisorMode
                                  ? instance.props.advisedForName
                                  : undefined
                              }
                              advisorMode={instance.props.advisorMode}
                              advisorRole={advisorRole}
                              onSuggestEOI={(investment, comment, completedCallback) =>
                                instance.suggestEOI(investment, comment, completedCallback)
                              }
                              onAcceptSuggestedEOI={(completedCallback) =>
                                instance.acceptSuggestedEOI(completedCallback)
                              }
                              onRejectSuggestedEOI={(completedCallback) =>
                                instance.rejectSuggestedEOI(completedCallback)
                              }
                              onWithdrawSuggestedEOI={(completedCallback) =>
                                instance.withdrawSuggestedEOI(completedCallback)
                              }
                              refreshInvestor={() => instance.refreshInvestorPreservingScroll()}
                              isCustodian={instance.state.isCustodian}
                            />
                          );
                        }}
                      </AdvisorAccessContextInstance.Consumer>
                    </GlobalConfigProvider>
                  )}
                </>
              )}
          </>
        </Box>
      </Box>
    </>
  );
}
